import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AuthContext } from "../../components/user/BCPAuthProvider";
import {
  getGameSystems,
  handlePaging
} from "../../components/api/APIUtilities";
import {
  Box,
  Button,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  useTheme
} from "@mui/material";

import { postNotification } from "../../components/utilities/Notifications";

import DatePickerComponent from "../../components/utilities/DatePickerComponent";
import moment from "moment";

import GameSystemPicker from "../../components/utilities/gamesystempickers/GameSystemPicker";
import { Formik } from "formik";
import * as Yup from "yup";

import GenericPicker from "../../components/utilities/GenericPicker";
import GoogleAddressSearch from "../../components/utilities/GoogleAddressSearch";
import ListResponsive from "../../components/other/ListResponsive";
import EventCard from "../../components/layout/EventCard";
import { globalVariables } from "../../global/GlobalVariables";
import {
  findGetParameter,
  getData,
  getDistance
} from "../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import {
  BCPArrowUpArrowDown,
  BCPDiceFive,
  BCPFilter,
  BCPMap,
  BCPSearch,
  BCPShareNodes,
  BCPXmark
} from "../../components/icons/bcp/BCPIcons";
import { stringToUrl, urlToString } from "./utils/event";
import ShareModal from "../../components/modals/ShareModal";
import {
  clearFilterSx,
  filterAndSortEvents,
  getFilterSx,
  updateFilters
} from "../../components/utilities/SortFilterFunctions";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { useTranslation } from "react-i18next";
import ListMap from "../../components/other/ListMap";
import OptionsMenu from "../../components/layout/OptionsMenu";
import { ActiveEventsModal } from "../organize/EventsOrganize/EventsOrganizeDefault";
import {
  getLocalStorage,
  hasCookiePermission,
  setLocalStorage,
  removeLocalStorage
} from "../../components/layout/cookies/CookieFunctions";
import EventSearchTokenTypes from "../../components/utilities/EventSearchTokenTypes";
import { getLanguageCookie } from "../../language/LanguageFunctions";

const GOLD_ORGANIZATION_ID = getEnvironmentVariable("GOLD_ORGANIZATION_ID");
const IS_GW = getEnvironmentVariable("COMPANY_FLAG") === "gw";
const isUsingV2API = IS_GW || getEnvironmentVariable("USE_V2") === "true";

const pathname = window?.location?.pathname || "/play/events";

const DEFAULT_EVENT_SEARCH_VALUES = {
  startDate: moment().startOf("day").subtract(3, "days").format("YYYY-MM-DD"),
  endDate: moment().add(2, "months").endOf("month").format("YYYY-MM-DD"),
  eventSubType: getEnvironmentVariable("COMPANY_FLAG") === "jasco" ? "LGS" : "",
  eventFormat: "",
  location: "Any Location",
  latitude: null,
  longitude: null,
  locationName: null,
  selectedAddress: "",
  selectedLocation: null,
  distance: 50,
  excludeOnline: IS_GW ? true : false,
  searchString: "",
  numberOfRounds: null,
  sortType: "startDateAscending",
  sortKey: "eventDate",
  eventStatus: "all",
  sortAscending: true
};

if (IS_GW) {
  DEFAULT_EVENT_SEARCH_VALUES.excludeOnline = true;
  DEFAULT_EVENT_SEARCH_VALUES.warhammerTO = true;
}

if (isUsingV2API) {
  DEFAULT_EVENT_SEARCH_VALUES.tokenTypes = [];
  DEFAULT_EVENT_SEARCH_VALUES.gameSystemId = "";
} else {
  DEFAULT_EVENT_SEARCH_VALUES.gameType = "";
}

const getCachedEventSearchValues = () => {
  const key = "eventSearchCache";
  const cachedDataFromWrapper = getLocalStorage(key);

  let cachedDataDirect = null;
  try {
    cachedDataDirect = localStorage.getItem(key);
  } catch (e) {
    console.error(
      `Events.js: Error calling localStorage.getItem('${key}') directly:`,
      e
    );
  }

  const dataToParse =
    cachedDataFromWrapper !== undefined
      ? cachedDataFromWrapper
      : cachedDataDirect;

  if (dataToParse) {
    try {
      const parsedData = JSON.parse(dataToParse);
      return parsedData;
    } catch (e) {
      console.error(
        `Events.js: Failed to parse cached data for '${key}':`,
        dataToParse,
        e
      );
      return null;
    }
  }
  return null;
};

const buildGameSystemDict = (gameSystems) => {
  var dict = {};
  gameSystems.forEach((gameSystem) => {
    dict[gameSystem.id] = gameSystem;
  });
  return dict;
};

const getSortOptions = (t) => [
  { label: t("startDateAscending"), ascending: true, field: "eventDate" },
  { label: t("startDateDescending"), ascending: false, field: "eventDate" },
  { label: t("eventNameAscending"), ascending: true, field: "searchString" },
  { label: t("eventNameDescending"), ascending: false, field: "searchString" },
  getEnvironmentVariable("SINGLE_GAME_SYSTEM")
    ? null
    : {
        label: `${
          getEnvironmentVariable("LABEL_GAMESYSTEM") || "Game"
        } Ascending`,
        ascending: true,
        field: "gameSystem"
      },
  getEnvironmentVariable("SINGLE_GAME_SYSTEM")
    ? null
    : {
        label: `${
          getEnvironmentVariable("LABEL_GAMESYSTEM") || "Game"
        } Descending`,
        ascending: false,
        field: "gameSystem"
      },
  { label: "Distance", ascending: true, field: "distance", type: "distance" },
  { label: "Rounds Descending", ascending: false, field: "numberOfRounds" }
];

export default function Events() {
  const { organizationId } = useParams();

  // translation setup
  const language = getLanguageCookie();
  const { t } = useTranslation("translation", {
    lng: language,
    keyPrefix: "fields"
  });
  const { t: tGeneral } = useTranslation();

  const searchUrlParam = findGetParameter("search");
  const onlineOnly = findGetParameter("onlineOnly");
  const excludeOnline = findGetParameter("excludeOnline");
  const years = findGetParameter("years");
  const months = findGetParameter("months");
  const eventFormatUrlParam = findGetParameter("eventFormat");
  const startDateUrlParam = findGetParameter("startDate");
  const endDateUrlParam = findGetParameter("endDate");
  const gameTypeUrlParam = findGetParameter("gameType");
  const gameSystemIdUrlParam = findGetParameter("gameSystemId");
  const subTypeUrlParam = findGetParameter("subType");
  const searchStringUrlParam = findGetParameter("searchString");
  const latUrlParam = findGetParameter("lat");
  const longUrlParam = findGetParameter("long");
  const distanceUrlParam = findGetParameter("distance");
  const distanceTypeUrlParam = findGetParameter("distanceType");
  const locationNameUrlParam = findGetParameter("locationName");
  const numbersOfRoundsUrlParam = findGetParameter("numberOfRounds");
  const sortTypeUrlParam = findGetParameter("sortType");
  const sortAscUrlParam = findGetParameter("sortAsc");
  const sortKeyUrlParam = findGetParameter("sortKey");
  const tokenTypesUrlParam = findGetParameter("tokenTypes");
  const orgIdUrlParam = findGetParameter("orgId");
  const eventStatusUrlParam = findGetParameter("eventStatus");
  const embed = findGetParameter("embed");

  const authContext = useContext(AuthContext);
  const globalVars = globalVariables();

  const [searchReady, setSearchReady] = useState(false);
  const [showSearch, setShowSearch] = useState(!Boolean(searchUrlParam));

  const [eventFormats, setEventFormats] = useState(null);

  const initalDistanceType =
    distanceTypeUrlParam || getLocalStorage("distanceType") || "miles";

  const [distanceType, setDistanceType] = useState(initalDistanceType);

  const [searchValues, setSearchValues] = useState(() => {
    let values = { ...DEFAULT_EVENT_SEARCH_VALUES };

    const cachedValues = getCachedEventSearchValues();
    if (cachedValues) {
      const originalCachedLocation = cachedValues.location;
      values = { ...values, ...cachedValues };

      if (originalCachedLocation === "Current Location") {
        values.location = "Current Location";
        values.selectedAddress = "";
        values.locationName = null;
      } else if (
        values.location &&
        typeof values.location === "string" &&
        values.location.startsWith("{")
      ) {
        try {
          const parsedApiLocation = JSON.parse(values.location);
          let displayLocationSet = false;

          if (values.selectedAddress) {
            values.location = values.selectedAddress;
            displayLocationSet = true;
          } else if (values.locationName) {
            values.location = values.locationName;
            displayLocationSet = true;
          }

          if (
            parsedApiLocation.center?.lat &&
            (values.latitude === null || values.latitude === undefined)
          ) {
            values.latitude = parsedApiLocation.center.lat.toString();
          }
          if (
            parsedApiLocation.center?.long &&
            (values.longitude === null || values.longitude === undefined)
          ) {
            values.longitude = parsedApiLocation.center.long.toString();
          }

          if (!displayLocationSet) {
            if (values.latitude && values.longitude) {
              values.location = values.locationName || "Shared Location";
            } else {
              values.location = DEFAULT_EVENT_SEARCH_VALUES.location;
            }
          }
        } catch (e) {
          console.error(
            "Events.js: Failed to parse cached stringified JSON location. Resetting location to default.",
            e
          );
          values.location = DEFAULT_EVENT_SEARCH_VALUES.location;
        }
      }
    }

    if (startDateUrlParam) {
      values.startDate = moment(startDateUrlParam)
        .startOf("day")
        .format("YYYY-MM-DD");
    }

    if (endDateUrlParam) {
      values.endDate = moment(endDateUrlParam)
        .startOf("day")
        .format("YYYY-MM-DD");
    } else if (years || months) {
      values.endDate = moment()
        .add(years ? (months ? parseInt(months) : 0) : 0, "months")
        .add(years ? parseInt(years) : 0, "years")
        .endOf("month")
        .format("YYYY-MM-DD");
    }

    if (gameTypeUrlParam) values.gameType = gameTypeUrlParam;
    if (gameSystemIdUrlParam) values.gameSystemId = gameSystemIdUrlParam;
    if (subTypeUrlParam) values.eventSubType = subTypeUrlParam;
    if (eventFormatUrlParam) values.eventFormat = eventFormatUrlParam;
    if (eventStatusUrlParam) values.eventStatus = eventStatusUrlParam;

    if (onlineOnly) {
      values.location = "Online Only";
      values.latitude = null;
      values.longitude = null;
      values.locationName = null;
      values.selectedAddress = "";
      values.selectedLocation = null;
    } else if (latUrlParam && longUrlParam) {
      values.latitude = latUrlParam;
      values.longitude = longUrlParam;
      values.locationName = locationNameUrlParam
        ? urlToString(locationNameUrlParam)
        : null;
      values.location = values.locationName
        ? values.locationName
        : "Shared Location";
      values.selectedAddress = values.locationName ? values.locationName : "";
      values.selectedLocation = null;
    }

    if (isUsingV2API && distanceTypeUrlParam) {
      values.location.distanceType = distanceTypeUrlParam;
    }

    if (distanceUrlParam) {
      values.distance = parseInt(distanceUrlParam);
    }

    if (excludeOnline !== null && excludeOnline !== undefined) {
      values.excludeOnline = excludeOnline === "true";
    } else if (onlineOnly) {
      values.excludeOnline = false;
    }

    if (searchStringUrlParam) {
      values.searchString = urlToString(searchStringUrlParam);
    }
    if (numbersOfRoundsUrlParam) {
      values.numberOfRounds = numbersOfRoundsUrlParam;
    }

    if (sortTypeUrlParam) values.sortType = urlToString(sortTypeUrlParam);
    if (sortKeyUrlParam) values.sortKey = sortKeyUrlParam;
    if (sortAscUrlParam !== null && sortAscUrlParam !== undefined) {
      values.sortAscending = sortAscUrlParam === "false" ? false : true;
    }
    return values;
  });
  const [nextKey, setNextKey] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filtersState, setFiltersState] = useState(null);

  const [sortLocation, setSortLocation] = useState(null);

  const [organization, setOrganization] = useState(null);

  const [, setGeoLocationError] = useState(null);
  const [geoLocation, setGeoLocation] = useState(null);
  const [locationOpen, setLocationOpen] = useState(false);

  var initalGameSystems = [];
  const [gameSystems, setGameSystems] = useState(initalGameSystems);
  const [gameSystemDict, setGameSystemDict] = useState(
    buildGameSystemDict(initalGameSystems)
  );
  const [eventListings, setEventListings] = useState(null);
  const [isSearching, setIsSearching] = useState(null);

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [searchFilters, setSearchFilters] = useState(null);
  const [shareSearchPath, setShareSearchPath] = useState(
    searchUrlParam
      ? window.location.pathname + (window.location.search || "")
      : null
  );

  const [filteredEvents, setFilteredEvents] = useState(null);
  const [filters, setFilters] = useState([]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorEl2, setAnchorEl2] = useState(null);
  const [anchorEl3, setAnchorEl3] = useState(null);

  const [toggleMap, setToggleMap] = useState(true);
  const [activeEvents, setActiveEvents] = useState(null);

  const [renderGameSystemFilters, setRenderGameSystemFilters] = useState(false);

  const navigate = useNavigate();
  const theme = useTheme();

  const search = async (filters, location, freshSearch) => {
    if (!filters && !filtersState) {
      postNotification(
        "error",
        tGeneral("notifications.errors.noFiltersSelected")
      );
      return;
    }
    if (filters) setFiltersState(filters);
    if (location) setSortLocation(location);

    var sFilters = filters || filtersState;
    var sLocation = location || sortLocation;

    if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
      sFilters.gameSystemId = getEnvironmentVariable("SINGLE_GAME_SYSTEM");
    } else if (getEnvironmentVariable("SINGLE_GAME_TYPE")) {
      sFilters.gameType = getEnvironmentVariable("SINGLE_GAME_TYPE");
    } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
      sFilters.gameSystemManufacturer = getEnvironmentVariable(
        "SINGLE_GAME_MANUFACTURER"
      );
    }

    const events = await handlePaging(
      "/events",
      sFilters,
      nextKey,
      setNextKey,
      isUsingV2API ? "v2" : "API"
    );

    if (events.length !== sFilters.limit) {
      setHasMore(false);
      if (!events?.length) {
        postNotification(
          "info",
          "No events found matching your search criteria."
        );
        setShowSearch(true);
        setIsSearching(false);
        return;
      }
    }

    // Hides gamesystem filter button if only one gamesystem is specified.
    if ((sFilters?.gameSystemId || sFilters?.gameType) && events?.length) {
      setRenderGameSystemFilters(false);
    } else {
      if (!renderGameSystemFilters) setRenderGameSystemFilters(true);
    }

    // Hides map if online only filter is selected and events are found
    if (sFilters?.onlineOnly && events?.length) {
      setToggleMap(false);
    } else {
      if (!toggleMap) setToggleMap(true);
    }

    events.forEach((event) => {
      if (event.gameSystemId && gameSystemDict[event.gameSystemId])
        event.gameSystem = gameSystemDict[event.gameSystemId];

      event.eventLocation = "";
      if (event.gameStoreName && event.gameStoreName !== "") {
        event.eventLocation = event.gameStoreName;
      }
      if (event.city && event.city !== "") {
        if (event.eventLocation !== "") {
          event.eventLocation += ", ";
        }
        event.eventLocation += event.city;
      }
      if (event.state && event.state !== "") {
        if (event.eventLocation !== "") {
          event.eventLocation += ", ";
        }
        event.eventLocation += event.state;
      }
      if (event.country && event.country !== "") {
        if (event.eventLocation !== "") {
          event.eventLocation += ", ";
        }
        event.eventLocation += event.country;
      }

      if (event.pricingDict) {
        var keys = Object.keys(event.pricingDict);
        if (keys.length > 0) {
          event.pricingInfo = {
            currency: keys[0],
            amount: (event.pricingDict[keys[0]] / 100).toFixed(2)
          };
        }
      }

      if (event.coordinate && sLocation?.center) {
        event.distanceType = distanceType;
        event.distance = getDistance(event.coordinate[1], event.coordinate[0], {
          latitude: sLocation.center.lat,
          longitude: sLocation.center.long
        });

        if (distanceType === "kms") {
          event.distance = event.distance / 0.6214;
        }
      }
    });

    setEventListings(
      !freshSearch && eventListings?.length
        ? [...eventListings, ...events]
        : events
    );
    setShowSearch(false);
    setIsSearching(false);
    setSearchFilters(filters);
  };

  const clearFilters = () => {
    updateFilters(null, null, null, setFilters);
    setRenderGameSystemFilters(false);
    setFilteredEvents(null);
    setToggleMap(true);
    setActiveEvents(null);
    setAnchorEl(null);
    setAnchorEl2(null);
    setAnchorEl3(null);
  };

  const resetEventSearch = () => {
    removeLocalStorage("eventSearchCache");
    setSearchValues(DEFAULT_EVENT_SEARCH_VALUES);
  };

  useEffect(() => {
    const check = async () => {
      if (onlineOnly) {
        if (searchValues.location !== "Online Only") {
          setSearchValues((prev) => ({
            ...prev,
            location: "Online Only",
            latitude: null,
            longitude: null,
            locationName: null,
            selectedAddress: ""
          }));
        }
      } else if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
          function (position) {
            setGeoLocation(position.coords);
            setGeoLocationError(null);
          },
          (err) => {
            setGeoLocationError(err);
            setSearchValues((prev) => {
              if (prev.location === "Current Location") {
                return { ...prev, location: "Any Location" };
              }
              return prev;
            });
          }
        );
      } else {
        setSearchValues((prev) => {
          if (prev.location === "Current Location") {
            return { ...prev, location: "Any Location" };
          }
          return prev;
        });
      }

      // If organizationId is set, fetch and set the org data
      if (organizationId) {
        try {
          const apiStore = await getData(`/gamestores/${organizationId}`);
          setOrganization(apiStore);
        } catch (err) {
          postNotification("error", err);
        }
      }

      var data = await getGameSystems();
      setGameSystems(data);
      setGameSystemDict(buildGameSystemDict(data));
      if (authContext?.authState?.cookieConsent) {
        try {
          setLocalStorage("gameSystems", JSON.stringify(data), authContext);
          var getCachedItems = getLocalStorage("CachedItems");
          var cachedItems = getCachedItems ? JSON.parse(getCachedItems) : {};
          cachedItems["gameSystems"] = true;
          setLocalStorage(
            "CachedItems",
            JSON.stringify(cachedItems),
            authContext
          );
        } catch (err) {}
      }

      if (getEnvironmentVariable("SUPPORTS_EVENT_FORMATS")) {
        var apiEventFormats = [];
        let foundEventFormatUrlParam = false;
        if (data?.length) {
          for (let gameSystem of data) {
            if (gameSystem.eventFormats?.list?.length) {
              for (let eventFormat of gameSystem.eventFormats.list) {
                if (!apiEventFormats.includes(eventFormat)) {
                  if (
                    eventFormatUrlParam &&
                    eventFormat.trim().toLowerCase() ===
                      eventFormatUrlParam.trim().toLowerCase()
                  ) {
                    setSearchValues((prevValues) => {
                      if (prevValues.eventFormat !== eventFormat) {
                        return { ...prevValues, eventFormat: eventFormat };
                      }
                      return prevValues;
                    });
                    foundEventFormatUrlParam = true;
                  }
                  apiEventFormats.push(eventFormat);
                }
              }
            }
          }
          if (apiEventFormats.length) {
            apiEventFormats.sort();
          }
        }
        if (eventFormatUrlParam && !foundEventFormatUrlParam) {
          setSearchValues((prevValues) => {
            if (
              prevValues.eventFormat === eventFormatUrlParam ||
              !prevValues.eventFormat
            ) {
              return { ...prevValues, eventFormat: "" };
            }
            return prevValues;
          });
        }
        setEventFormats(apiEventFormats?.length ? apiEventFormats : null);
      }

      setSearchReady(true);
    };
    check();
    // eslint-disable-next-line
  }, []);

  // If search url param is true, skip search form and go straight to results based on url params
  useEffect(() => {
    const go = async () => {
      try {
        let filters = {
          limit: 40,
          sortAscending: sortAscUrlParam === "false" ? false : true,
          sortKey: sortKeyUrlParam ? sortKeyUrlParam : "eventDate"
        };

        let searchLocation;

        if (startDateUrlParam && endDateUrlParam) {
          if (moment(startDateUrlParam).isAfter(moment(endDateUrlParam))) {
            throw new Error("Start Date must be before the End Date");
          }
        }

        if (organizationId) {
          filters.gameStoreId = organizationId;
        }

        if (eventStatusUrlParam) {
          if (eventStatusUrlParam === "all") {
            // Do nothing, all events will be returned
          } else if (eventStatusUrlParam === "not-started") {
            filters.started = false;
            filters.ended = false;
          } else if (eventStatusUrlParam === "in-progress") {
            filters.started = true;
            filters.ended = false;
          } else if (eventStatusUrlParam === "ended") {
            filters.ended = true;
          }
        }

        if (startDateUrlParam) {
          filters.startDate = moment(startDateUrlParam, "YYYY-MM-DD HH:mm")
            .utc()
            .format();
        } else {
          filters.startDate = moment().startOf("day").utc().format();
        }

        if (endDateUrlParam) {
          filters.endDate = moment(endDateUrlParam, "YYYY-MM-DD HH:mm")
            .utc()
            .format();
        } else {
          filters.endDate = moment()
            .add(years ? (months ? parseInt(months) : 0) : 2, "months")
            .add(years ? parseInt(years) : 0, "years")
            .endOf("month")
            .utc()
            .format();
        }

        if (gameTypeUrlParam) filters.gameType = gameTypeUrlParam;

        if (gameSystemIdUrlParam) filters.gameSystemId = gameSystemIdUrlParam;

        if (subTypeUrlParam && globalVars?.eventSubTypes?.list?.length)
          filters.eventSubType = urlToString(subTypeUrlParam);

        if (eventFormatUrlParam && process.env.REACT_APP_SUPPORTS_EVENT_FORMATS)
          filters.eventFormat = urlToString(eventFormatUrlParam);

        if (numbersOfRoundsUrlParam)
          filters.numberOfRounds = parseInt(numbersOfRoundsUrlParam);

        if (excludeOnline) filters.excludeOnline = true;

        if (searchStringUrlParam)
          filters.searchString = urlToString(searchStringUrlParam);

        if (latUrlParam && longUrlParam) {
          searchLocation = {
            distance: distanceUrlParam || 50,
            center: {
              lat: latUrlParam,
              long: longUrlParam
            }
          };

          searchLocation.distanceType = distanceTypeUrlParam || "miles";

          filters.location = JSON.stringify(searchLocation);

          if (locationNameUrlParam) {
            filters.selectedAddress = urlToString(locationNameUrlParam);
          }

          if (distanceTypeUrlParam) {
            filters.distanceType = distanceTypeUrlParam;
          }
        } else {
          if (onlineOnly) {
            filters.onlineOnly = true;
          }
        }

        if (tokenTypesUrlParam) {
          const tokenTypesArray = urlToString(tokenTypesUrlParam)
            .split(",")
            .map((token) => token.trim());
          filters.tokenTypes = tokenTypesArray;
        }

        await search(filters, searchLocation, true);
      } catch (err) {
        postNotification("error", err);
      }
    };

    if (searchUrlParam) {
      go();
    }
    // eslint-disable-next-line
  }, []);

  // Create url path with selected filters
  useEffect(() => {
    let newPath = "";

    if (searchFilters && searchFilters !== undefined) {
      newPath = `${pathname}?search=true&${
        embed === "true" ? "embed=true&" : ""
      }`;

      if (searchFilters?.startDate) {
        newPath += `startDate=${moment(searchFilters.startDate)
          .startOf("day")
          .format("YYYY-MM-DD")}&`;
      }

      if (searchFilters?.endDate) {
        newPath += `endDate=${moment(searchFilters.endDate)
          .endOf("day")
          .format("YYYY-MM-DD")}&`;
      }

      if (searchFilters?.searchString) {
        newPath += `searchString=${stringToUrl(searchFilters.searchString)}&`;
      }

      if (searchFilters?.gameType) {
        newPath += `gameType=${searchFilters.gameType}&`;
      }

      if (searchFilters?.gameSystemId) {
        newPath += `gameSystemId=${searchFilters.gameSystemId}&`;
      }

      if (
        searchFilters?.eventSubType &&
        globalVars?.eventSubTypes?.list?.length
      ) {
        newPath += `subType=${stringToUrl(searchFilters.eventSubType)}&`;
      }

      if (searchFilters?.excludeOnline) {
        newPath += `excludeOnline=true&`;
      }

      if (
        searchFilters?.eventFormat &&
        process.env.REACT_APP_SUPPORTS_EVENT_FORMATS
      ) {
        newPath += `eventFormat=${stringToUrl(searchFilters.eventFormat)}&`;
      }

      if (searchFilters?.location) {
        let tempLocation = JSON.parse(searchFilters?.location);
        newPath += `distance=${tempLocation.distance}&lat=${tempLocation.center.lat}&long=${tempLocation.center.long}&`;

        if (searchFilters?.distanceType) {
          newPath += `distanceType=${searchFilters.distanceType}&`;
        }

        if (searchFilters?.selectedAddress || searchValues?.locationName) {
          newPath += `locationName=${stringToUrl(
            searchFilters?.selectedAddress || searchValues?.locationName
          )}&`;
        }
      }

      if (searchFilters?.onlineOnly) {
        newPath += `onlineOnly=true&`;
      }

      if (searchFilters?.numberOfRounds) {
        newPath += `numberOfRounds=${searchFilters.numberOfRounds}&`;
      }

      if (searchFilters?.sortType) {
        newPath += `sortType=${stringToUrl(searchFilters.sortType)}&`;
      }

      if (searchFilters?.sortAsc) {
        newPath += `sortAsc=${searchFilters.sortAsc}&`;
      } else {
        newPath += `sortAsc=false&`;
      }

      if (searchFilters?.ended) {
        newPath += `eventStatus=ended&`;
      } else if (searchFilters?.started) {
        newPath += `eventStatus=in-progress&`;
      } else {
        if (searchFilters?.started === false) {
          newPath += `eventStatus=not-started&`;
        } else {
          newPath += `eventStatus=all&`;
        }
      }

      if (searchFilters?.sortKey) {
        newPath += `sortKey=${searchFilters.sortKey}&`;
      }

      if (searchFilters?.tokenTypes && searchFilters?.tokenTypes?.length) {
        const tokenString = searchFilters.tokenTypes.join(",").trim();

        newPath += `tokenTypes=${stringToUrl(tokenString)}&`;
      }

      if (orgIdUrlParam) {
        newPath += `orgId=${orgIdUrlParam}&`;
      }

      newPath = newPath.substring(0, newPath.length - 1);
    }

    if (newPath !== "") setShareSearchPath(newPath);

    // eslint-disable-next-line
  }, [searchFilters]);

  // Updates url when switching between search and results
  useEffect(() => {
    if (!showSearch && shareSearchPath) {
      navigate(shareSearchPath);
    } else if (!showSearch) {
      navigate(`${pathname}${embed === "true" ? "?embed=true" : ""}`);
    }
    // eslint-disable-next-line
  }, [showSearch]);

  // Filter and sort events
  useEffect(() => {
    if (eventListings?.length && filters.length) {
      const tempEvents = filterAndSortEvents(eventListings, filters);

      setFilteredEvents(tempEvents);
    } else if (!filters.length) {
      setFilteredEvents(null);
    }
    // eslint-disable-next-line
  }, [filters, eventListings]);

  return (
    <Box sx={{ width: "100%" }}>
      <Container
        sx={{
          pt: 2,
          pb: 2
        }}
        maxWidth="lg"
      >
        <Grid container sx={{ spacing: 2, p: 2 }}>
          <Grid
            item
            xs={12}
            md={9}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <Typography
              variant="h2"
              component="h3"
              fontWeight="700"
              gutterBottom
            >
              {`${t("title", { keyPrefix: "pages.eventListings" })}${organization ? ` - ${organization.name}` : ""}`}
            </Typography>
            {!showSearch && (
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setShareModalOpen(true)}
                sx={{ mr: [0, 1] }}
              >
                <BCPShareNodes />
              </IconButton>
            )}
          </Grid>
          {!showSearch && (
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                color="warning"
                onClick={() => {
                  setNextKey(null);
                  setShowSearch(true);
                  clearFilters();

                  navigate(
                    `${pathname}${embed === "true" ? "?embed=true" : ""}`,
                    { replace: false }
                  );
                }}
                fullWidth
                startIcon={<BCPSearch />}
              >
                {t("editSearch")}
              </Button>
            </Grid>
          )}

          {showSearch && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Formik
                initialValues={searchValues}
                enableReinitialize={true}
                validationSchema={Yup.object().shape({
                  searchString: Yup.string()
                    .min(3, "Minimum 3 Characters!")
                    .max(255, "Maximum 255 Characters!")
                })}
                onSubmit={async (values, { setSubmitting, setFieldValue }) => {
                  try {
                    setIsSearching(true);
                    setEventListings([]);
                    var filters = {
                      limit: 40,
                      startDate: moment(values.startDate, "YYYY-MM-DD HH:mm")
                        .utc()
                        .format(),
                      endDate: moment(values.endDate, "YYYY-MM-DD HH:mm")
                        .utc()
                        .format(),
                      sortKey: values.sortKey || "eventDate",
                      sortAscending: values.sortAscending
                    };

                    filters.sortAsc = values.sortAscending;

                    if (values.eventStatus === "all") {
                      // Do nothing, all events will be returned
                    } else if (values.eventStatus === "not-started") {
                      filters.started = false;
                      filters.ended = false;
                    } else if (values.eventStatus === "in-progress") {
                      filters.started = true;
                      filters.ended = false;
                    } else if (values.eventStatus === "ended") {
                      filters.ended = true;
                    }

                    if (values.sortType) {
                      filters.sortType = values.sortType;
                    }

                    if (values.sortKey) {
                      filters.sortKey = values.sortKey;
                    } else {
                      filters.sortKey = "eventDate";
                    }

                    if (values?.tokenTypes?.length) {
                      filters.tokenTypes = values.tokenTypes;
                    }

                    if (
                      moment(filters.startDate).isAfter(moment(filters.endDate))
                    ) {
                      throw new Error("Start Date must be before the End Date");
                    }
                    if (organizationId) {
                      filters.gameStoreId = organizationId;
                    }
                    if (typeof values.gameType === "number") {
                      filters.gameType = values.gameType;
                    }
                    if (values.gameSystemId && values.gameSystemId !== "") {
                      filters.gameSystemId = values.gameSystemId;
                    }
                    if (values.eventSubType && values.eventSubType !== "") {
                      filters.eventSubType = values.eventSubType;
                    }
                    if (values.eventFormat && values.eventFormat !== "") {
                      filters.eventFormat = values.eventFormat;
                    }
                    if (values.numberOfRounds && values.numberOfRounds !== "") {
                      filters.numberOfRounds = values.numberOfRounds;
                    }

                    if (values.excludeOnline) {
                      filters.excludeOnline = true;
                    }

                    var location;

                    if (values.location === "Current Location") {
                      if (geoLocation?.latitude && geoLocation?.longitude) {
                        location = {
                          distance: values.distance,
                          center: {
                            lat: geoLocation.latitude,
                            long: geoLocation.longitude
                          }
                        };
                      }
                    } else if (
                      values.location === searchValues?.locationName ||
                      values.location === "Shared Location"
                    ) {
                      location = {
                        distance: values.distance,
                        center: {
                          lat: searchValues.latitude,
                          long: searchValues.longitude
                        }
                      };
                    } else if (values.location === "Online Only") {
                      filters.onlineOnly = true;
                    } else if (
                      values.location !== "Any Location" &&
                      values.location !== "Online Only" &&
                      values.location !== "Current Location" &&
                      !values.selectedLocation &&
                      values.latitude &&
                      values.longitude &&
                      (values.location === values.selectedAddress ||
                        values.location === values.locationName)
                    ) {
                      location = {
                        distance: values.distance,
                        center: {
                          lat: values.latitude,
                          long: values.longitude
                        }
                      };
                    } else if (
                      values.location !== "Any Location" &&
                      values.selectedLocation?.coordinate?.length === 2
                    ) {
                      location = {
                        distance: values.distance,
                        center: {
                          lat: values.selectedLocation.coordinate[1],
                          long: values.selectedLocation.coordinate[0]
                        }
                      };
                    }

                    if (location) {
                      if (isUsingV2API) location.distanceType = distanceType;

                      if (values.selectedAddress) {
                        filters.selectedAddress = values.selectedAddress;
                      }
                      if (distanceType === "kms") {
                        location.distance = location.distance * 0.6214;
                        filters.distanceType = "kms";
                      } else {
                        filters.distanceType = "miles";
                      }
                      filters.location = JSON.stringify(location);
                    }

                    if (values.searchString?.length > 0) {
                      filters.searchString = values.searchString.toLowerCase();
                    }

                    setHasMore(true);
                    setSearchValues(values);
                    let formValuesForCaching = { ...values };
                    if (
                      formValuesForCaching.location === "Current Location" &&
                      geoLocation?.latitude &&
                      geoLocation?.longitude
                    ) {
                      formValuesForCaching.latitude = geoLocation.latitude;
                      formValuesForCaching.longitude = geoLocation.longitude;
                    } else if (
                      formValuesForCaching.selectedLocation?.coordinate
                        ?.length === 2
                    ) {
                      formValuesForCaching.latitude =
                        formValuesForCaching.selectedLocation.coordinate[1];
                      formValuesForCaching.longitude =
                        formValuesForCaching.selectedLocation.coordinate[0];
                    }

                    if (authContext?.authState?.cookieConsent) {
                      const { selectedLocation, ...relevantFormValues } =
                        formValuesForCaching;

                      const valuesToCache = {
                        ...DEFAULT_EVENT_SEARCH_VALUES,
                        ...relevantFormValues,
                        latitude: relevantFormValues.latitude,
                        longitude: relevantFormValues.longitude
                      };
                      setLocalStorage(
                        "eventSearchCache",
                        JSON.stringify(valuesToCache),
                        authContext
                      );
                    }
                    setSearchFilters(filters);

                    await search(filters, location, true);
                  } catch (error) {
                    postNotification("error", error);
                    setIsSearching(false);
                  }
                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  touched,
                  values,
                  setFieldValue
                }) => (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid
                        item
                        xs={12}
                        md={values.location === "Online Only" || IS_GW ? 12 : 6}
                      >
                        <GenericPicker
                          label={t("location")}
                          inclAny={false}
                          name="location"
                          list={[
                            <Box
                              sx={{ p: 1 }}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <GoogleAddressSearch
                                setLocationData={(e) => {
                                  setFieldValue(
                                    "selectedAddress",
                                    e?.formatted_address
                                      ? e.formatted_address
                                      : ""
                                  );
                                  setFieldValue(
                                    "location",
                                    e?.formatted_address
                                      ? e.formatted_address
                                      : "Current Location"
                                  );
                                  setFieldValue("selectedLocation", e);
                                  setFieldValue(
                                    "latitude",
                                    e?.geometry?.location?.lat()
                                  );
                                  setFieldValue(
                                    "longitude",
                                    e?.geometry?.location?.lng()
                                  );
                                  setLocationOpen(false);
                                }}
                              />
                            </Box>,
                            values.selectedAddress &&
                            values.location === values.selectedAddress &&
                            values.location !== "Current Location"
                              ? values.selectedAddress
                              : null,
                            (values.latitude &&
                              values.longitude &&
                              !values.selectedAddress &&
                              values.location !== "Current Location") ||
                            values.location === "Shared Location"
                              ? t("sharedLocation")
                              : null,
                            geoLocation ||
                            values.location === "Current Location"
                              ? t("currentLocation")
                              : null,
                            t("anyLocation"),
                            IS_GW ? null : t("onlineOnly")
                          ]}
                          value={
                            values.location === "Any Location"
                              ? t("anyLocation")
                              : values.location === "Current Location"
                                ? t("currentLocation")
                                : values.location === "Shared Location"
                                  ? t("sharedLocation")
                                  : values.location === "Online Only"
                                    ? t("onlineOnly")
                                    : values.location
                          }
                          handleChange={(e) => {
                            const newLocationValue = e.target.value;
                            handleChange(e);
                            if (newLocationValue === "Current Location") {
                              setFieldValue("selectedAddress", "");
                              setFieldValue("locationName", null);
                            } else if (
                              newLocationValue === "Any Location" ||
                              newLocationValue === "Online Only"
                            ) {
                              setFieldValue("selectedAddress", "");
                              setFieldValue("locationName", null);
                              setFieldValue("latitude", null);
                              setFieldValue("longitude", null);
                            }
                          }}
                          open={locationOpen}
                          setOpen={setLocationOpen}
                        />
                      </Grid>
                      {values.location !== "Any Location" &&
                        values.location !== "Online Only" && (
                          <Grid
                            item
                            xs={12}
                            sm={IS_GW ? 12 : 6}
                            md={IS_GW ? 12 : 3}
                          >
                            <TextField
                              name="distance"
                              label={`Distance (in ${distanceType})`}
                              value={values.distance}
                              variant="outlined"
                              fullWidth
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.distance && errors.distance
                              )}
                              helperText={touched.distance && errors.distance}
                              type="number"
                              inputProps={{
                                min: 0
                              }}
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <GenericPicker
                                      inclAny={false}
                                      list={["miles", "kms"]}
                                      value={distanceType}
                                      formControlSX={{
                                        p: 0,
                                        mt: 0,
                                        mb: 0,
                                        "& fieldset": {
                                          borderWidth: 0
                                        },
                                        "& .MuiOutlinedInput-root": {
                                          border: 0
                                        },
                                        "& .MuiSelect-select": {
                                          p: "4px"
                                        }
                                      }}
                                      handleChange={(e) => {
                                        setDistanceType(e.target.value);
                                        if (
                                          authContext?.authState?.cookieConsent
                                        ) {
                                          try {
                                            setLocalStorage(
                                              "distanceType",
                                              e.target.value,
                                              authContext
                                            );
                                            var getCachedItems =
                                              getLocalStorage("CachedItems");
                                            var cachedItems = getCachedItems
                                              ? JSON.parse(getCachedItems)
                                              : {};
                                            cachedItems["distanceType"] = true;
                                            setLocalStorage(
                                              "CachedItems",
                                              JSON.stringify(cachedItems),
                                              authContext
                                            );
                                          } catch (err) {}
                                        }
                                      }}
                                    />
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Grid>
                        )}
                      {values.location !== "Online Only" && !IS_GW && (
                        <Grid
                          item
                          xs={12}
                          sm={values.location !== "Any Location" ? 6 : 12}
                          md={values.location !== "Any Location" ? 3 : 6}
                        >
                          <FormControlLabel
                            label={
                              <Typography className="MuiFormControlLabel-label">
                                {t("includeOnline")}
                              </Typography>
                            }
                            sx={{
                              ml: 0,
                              width: "100%",
                              "& .MuiFormControlLabel-label": {
                                width: "calc(100% - 58px)"
                              }
                            }}
                            control={
                              <Switch
                                color="primary"
                                checked={!values.excludeOnline}
                                onChange={(e) => {
                                  setFieldValue(
                                    "excludeOnline",
                                    !values.excludeOnline
                                  );
                                }}
                              />
                            }
                            labelPlacement="start"
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <DatePickerComponent
                          name="startDate"
                          label={t("startDate")}
                          value={values.startDate}
                          setFieldValue={setFieldValue}
                          error={Boolean(
                            touched.startDate && values.startDate === ""
                          )}
                          helperText={
                            touched.startDate && values.startDate === ""
                              ? "Required"
                              : null
                          }
                          data-dd-privacy={
                            hasCookiePermission("dataDog") ? "allow" : "mask"
                          }
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DatePickerComponent
                          name="endDate"
                          label={t("endDate")}
                          value={values.endDate}
                          setFieldValue={setFieldValue}
                          error={Boolean(
                            touched.endDate && values.endDate === ""
                          )}
                          helperText={
                            touched.endDate && values.endDate === ""
                              ? "Required"
                              : null
                          }
                          data-dd-privacy={
                            hasCookiePermission("dataDog") ? "allow" : "mask"
                          }
                        />
                      </Grid>
                      {!getEnvironmentVariable("SINGLE_GAME_SYSTEM") && (
                        <Grid item xs={12}>
                          <GameSystemPicker
                            name={isUsingV2API ? "gameSystemId" : "gameType"}
                            value={
                              isUsingV2API
                                ? values.gameSystemId
                                : values.gameType
                            }
                            valueType={isUsingV2API ? "id" : "code"}
                            handleChange={handleChange}
                            gameSystems={gameSystems}
                            touched={
                              isUsingV2API
                                ? touched.gameSystemId
                                : touched.gameType
                            }
                            error={
                              isUsingV2API
                                ? errors.gameSystemId
                                : errors.gameType
                            }
                            allowAny
                            data-dd-privacy={
                              hasCookiePermission("dataDog") ? "allow" : "mask"
                            }
                          />
                        </Grid>
                      )}
                      {globalVars?.eventSubTypes?.list?.length > 0 && (
                        <Grid item xs={12}>
                          <GenericPicker
                            name="eventSubType"
                            label={
                              globalVars?.eventSubTypes?.label ||
                              "Event Sub Type"
                            }
                            value={values.eventSubType}
                            handleChange={handleChange}
                            list={globalVars.eventSubTypes.list || []}
                            inclAny={true}
                            data-dd-privacy={
                              hasCookiePermission("dataDog") ? "allow" : "mask"
                            }
                          />
                        </Grid>
                      )}
                      {getEnvironmentVariable("SUPPORTS_EVENT_FORMATS") && (
                        <Grid item xs={12}>
                          <GenericPicker
                            name="eventFormat"
                            label={getEnvironmentVariable(
                              "SUPPORTS_EVENT_FORMAT_LABEL"
                            )}
                            value={values.eventFormat}
                            handleChange={handleChange}
                            list={eventFormats || []}
                            inclAny={true}
                            data-dd-privacy={
                              hasCookiePermission("dataDog") ? "allow" : "mask"
                            }
                          />
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="searchString"
                          label={t("eventName")}
                          value={values.searchString}
                          variant="outlined"
                          fullWidth
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.searchString && errors.searchString
                          )}
                          helperText={
                            touched.searchString && errors.searchString
                          }
                          data-dd-privacy={
                            hasCookiePermission("dataDog") ? "allow" : "mask"
                          }
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <GenericPicker
                          label="Event Status"
                          inclAny={false}
                          name="eventStatus"
                          list={[
                            { label: "All Events", value: "all" },
                            { label: "Not Started", value: "not-started" },
                            { label: "In Progress", value: "in-progress" },
                            { label: "Ended", value: "ended" }
                          ]}
                          value={values.eventStatus}
                          handleChange={(e) => {
                            setFieldValue("eventStatus", e.target?.value);
                          }}
                        />
                      </Grid>

                      {isUsingV2API &&
                      (orgIdUrlParam ||
                        organizationId ||
                        GOLD_ORGANIZATION_ID) ? (
                        <Grid item xs={12}>
                          <EventSearchTokenTypes
                            organizationId={
                              orgIdUrlParam ||
                              organizationId ||
                              GOLD_ORGANIZATION_ID
                            }
                            value={values.tokenTypes}
                            setFieldValue={setFieldValue}
                            name="tokenTypes"
                            label="Token Types (Optional)"
                            formControlSX={{ mt: 0, mb: 0 }}
                          />
                        </Grid>
                      ) : (
                        <></>
                      )}

                      <Grid item xs={12} sm={6}>
                        <TextField
                          name="numberOfRounds"
                          label={t("minimumRounds")}
                          value={values.numberOfRounds}
                          variant="outlined"
                          fullWidth
                          type="number"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.numberOfRounds && errors.numberOfRounds
                          )}
                          helperText={
                            touched.numberOfRounds && errors.numberOfRounds
                          }
                          data-dd-privacy={
                            hasCookiePermission("dataDog") ? "allow" : "mask"
                          }
                          inputProps={{
                            min: 1
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <GenericPicker
                          label={t("sort")}
                          inclAny={false}
                          name="sortOrder"
                          list={getSortOptions(t).filter((el) => {
                            return (
                              el &&
                              !(
                                el.type === "distance" &&
                                (values.location === "Any Location" ||
                                  values.location === "Online Only")
                              )
                            );
                          })}
                          value={t(values.sortType)}
                          handleChange={(e) => {
                            setFieldValue("sortType", e.target?.value);
                            var sortField = getSortOptions(t).filter((el) => {
                              return el?.label && el.label === e.target?.value;
                            })[0];
                            setFieldValue("sortKey", sortField?.field);
                            setFieldValue(
                              "sortAscending",
                              sortField?.ascending
                            );
                          }}
                        />
                      </Grid>

                      <Grid item xs={8} md={9}>
                        <Button
                          type="submit"
                          color={"primary"}
                          variant={"contained"}
                          fullWidth
                          startIcon={<BCPSearch />}
                          sx={{ textTransform: "uppercase" }}
                        >
                          {t("search")}
                        </Button>
                      </Grid>
                      <Grid item xs={4} md={3}>
                        <Button
                          type="button"
                          color={"warning"}
                          variant={"contained"}
                          fullWidth
                          startIcon={<BCPXmark />}
                          sx={{ textTransform: "uppercase" }}
                          onClick={resetEventSearch}
                        >
                          {t("reset")}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                )}
              </Formik>
            </Grid>
          )}
          {isSearching !== null && !showSearch && (
            <Grid
              item
              xs={12}
              sx={{
                p: 2
              }}
            >
              <ListResponsive
                originalList={
                  filteredEvents !== null ? filteredEvents : eventListings
                }
                doSearch
                searchProps={{
                  label: "Search Results",
                  performSearch: (originalList, searchText) => {
                    if (!searchText || searchText === "") {
                      return originalList;
                    } else {
                      var filteredList = originalList.filter(function (el) {
                        if (el?.name?.toLowerCase().includes(searchText))
                          return true;
                        if (
                          el?.eventLocation?.toLowerCase().includes(searchText)
                        )
                          return true;
                        if (
                          el?.gameSystem?.name
                            ?.toLowerCase()
                            .includes(searchText)
                        )
                          return true;
                        if (
                          el?.gameStoreName?.toLowerCase().includes(searchText)
                        )
                          return true;
                        if (
                          el?.ownerFirstName?.toLowerCase().includes(searchText)
                        )
                          return true;
                        if (
                          el?.ownerLastName?.toLowerCase().includes(searchText)
                        )
                          return true;
                        return false;
                      });
                      return filteredList;
                    }
                  }
                }}
                loadMore={search}
                hasMore={hasMore}
                scrollToTop
                actionButtonBottom={[
                  {
                    label: "Sort",
                    startIcon: <BCPArrowUpArrowDown />,
                    onClick: (e) => {
                      setAnchorEl(e.currentTarget);
                    },
                    disabled: !eventListings?.length,
                    sx: {
                      ...getFilterSx(
                        Boolean(
                          filters.length &&
                            filters.some((item) => item.includes("sort"))
                        ),
                        filters,
                        theme
                      )
                    }
                  },
                  {
                    label: "Filters",
                    startIcon: <BCPFilter />,
                    onClick: (e) => {
                      setAnchorEl2(e.currentTarget);
                    },
                    disabled: !eventListings?.length,
                    sx: {
                      ...getFilterSx(
                        Boolean(
                          filters.length &&
                            filters.some((item) => item.includes("filter"))
                        ),
                        filters,
                        theme
                      )
                    }
                  },
                  getEnvironmentVariable("SINGLE_GAME_SYSTEM") !== "true" &&
                    getEnvironmentVariable("SINGLE_GAME_MANUFACTURER") !==
                      "true" &&
                    renderGameSystemFilters && {
                      label: t("gameSystem"),
                      startIcon: <BCPDiceFive />,
                      disabled: !eventListings?.length || !gameSystems?.length,
                      onClick: (e) => {
                        setAnchorEl3(e.currentTarget);
                      },
                      sx: {
                        ...getFilterSx(
                          Boolean(
                            filters.length &&
                              filters.some((item) =>
                                item.includes("gamesystem")
                              )
                          ),
                          filters,
                          theme
                        )
                      }
                    },
                  {
                    label: "Toggle Map",
                    startIcon: <BCPMap />,
                    onClick: (e) => {
                      setToggleMap(!toggleMap);
                    },
                    disabled: !eventListings?.length,
                    sx: {
                      ...getFilterSx(toggleMap, filters, theme)
                    }
                  },
                  {
                    label: "Clear Filters",
                    startIcon: <BCPXmark />,

                    disabled: !eventListings?.length,
                    sx: {
                      ...clearFilterSx(false, filters, theme)
                    },
                    onClick: clearFilters
                  }
                ]}
                rowProps={{
                  mapFunction: (row, index) => (
                    <Grid key={index} item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                      <EventCard
                        event={row}
                        key={row?.id || index}
                        authContext={authContext}
                        gameSystemDict={gameSystemDict}
                        showStatus
                        preventImageFetch
                      />
                    </Grid>
                  )
                }}
              >
                {toggleMap && (
                  <Box sx={{ mt: [0, 2] }}>
                    <ListMap
                      name="events-map"
                      originalList={
                        filteredEvents !== null
                          ? filteredEvents?.filter(
                              (event) => !event?.isOnlineEvent
                            )
                          : eventListings
                            ? eventListings.filter(
                                (event) => !event?.isOnlineEvent
                              )
                            : null
                      }
                      zoom={9}
                      sx={{ mb: 1 }}
                      mapSx={{ height: "30vh" }}
                      defaultCenter={
                        filteredEvents?.length
                          ? {
                              lat: filteredEvents?.[0]?.coordinate?.[1],
                              lng: filteredEvents?.[0]?.coordinate?.[0]
                            }
                          : eventListings?.length
                            ? {
                                lat: eventListings?.[0]?.coordinate?.[1],
                                lng: eventListings?.[0]?.coordinate?.[0]
                              }
                            : null
                      }
                      onClick={(lat, lng) => {
                        const eventArr = [];
                        eventListings.forEach((event) => {
                          if (
                            event?.coordinate?.[0] === lng &&
                            event?.coordinate?.[1] === lat
                          ) {
                            eventArr.push(event);
                          }
                        });

                        setActiveEvents(eventArr);
                      }}
                      filterFunction={(event) =>
                        !event?.isOnlineEvent &&
                        (event?.coordinate?.length ||
                          event?.coordinate_point?.coordinates?.length) &&
                        (event?.coordinate[0] ||
                          event?.coordinate_point?.coordinates[0]) &&
                        (event?.coordinate[1] ||
                          event?.coordinate_point?.coordinates[1])
                      }
                    />
                  </Box>
                )}
              </ListResponsive>
            </Grid>
          )}

          {/* Sorting */}
          <OptionsMenu
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            items={[
              {
                name: "sort-date-asc",
                label: "Start Date Ascending",
                selected: filters.includes("sort-date-asc"),
                onClick: () =>
                  updateFilters("sort-date-asc", false, filters, setFilters)
              },
              {
                name: "sort-date-desc",
                label: "Start Date Descending",
                selected: filters.includes("sort-date-desc"),
                onClick: () =>
                  updateFilters("sort-date-desc", false, filters, setFilters)
              },
              {
                name: "sort-name-asc",
                label: "Name Ascending",
                selected: filters.includes("sort-name-asc"),
                onClick: () =>
                  updateFilters("sort-name-asc", false, filters, setFilters)
              },
              {
                name: "sort-name-desc",
                label: "Name Descending",
                selected: filters.includes("sort-name-desc"),
                onClick: () =>
                  updateFilters("sort-name-desc", false, filters, setFilters)
              },
              {
                name: "sort-rounds-asc",
                label: "Rounds Ascending",
                selected: filters.includes("sort-rounds-asc"),
                onClick: () =>
                  updateFilters("sort-rounds-asc", false, filters, setFilters)
              },
              {
                name: "sort-rounds-desc",
                label: "Rounds Descending",
                selected: filters.includes("sort-rounds-desc"),
                onClick: () =>
                  updateFilters("sort-rounds-desc", false, filters, setFilters)
              }
            ]}
            name="events-sort-menu"
          />

          {/* Filters */}
          <OptionsMenu
            anchorEl={anchorEl2}
            setAnchorEl={setAnchorEl2}
            items={[
              {
                name: "filter-online",
                label: "Online Event",
                selected: filters.includes("filter-online"),
                onClick: () =>
                  updateFilters(
                    "filter-online",
                    "filter-exclude-online",
                    filters,
                    setFilters
                  )
              },
              {
                name: "filter-exclude-online",
                label: "Exclude Online",
                selected: filters.includes("filter-exclude-online"),
                onClick: () =>
                  updateFilters(
                    "filter-exclude-online",
                    "filter-online",
                    filters,
                    setFilters
                  )
              },
              { divider: true },

              {
                name: "filter-org",
                label: "Game Store Events",
                selected: filters.includes("filter-org"),
                onClick: () =>
                  updateFilters(
                    "filter-org",
                    "filter-exclude-org",
                    filters,
                    setFilters
                  )
              },
              {
                name: "filter-exclude-org",
                label: "Exclude Game Store Events",
                selected: filters.includes("filter-exclude-org"),
                onClick: () =>
                  updateFilters(
                    "filter-exclude-org",
                    "filter-org",
                    filters,
                    setFilters
                  )
              },
              { divider: true },

              {
                name: "filter-singles",
                label: "Singles Events",
                selected: filters.includes("filter-singles"),
                onClick: () =>
                  updateFilters(
                    "filter-singles",
                    ["filter-doubles", "filter-teams", "filter-board"],
                    filters,
                    setFilters
                  )
              },
              {
                name: "filter-doubles",
                label: "Doubles Events",
                selected: filters.includes("filter-doubles"),
                onClick: () =>
                  updateFilters(
                    "filter-doubles",
                    ["filter-singles", "filter-teams", "filter-board"],
                    filters,
                    setFilters
                  )
              },
              {
                name: "filter-teams",
                label: "Teams Events",
                selected: filters.includes("filter-teams"),
                onClick: () =>
                  updateFilters(
                    "filter-teams",
                    ["filter-singles", "filter-doubles", "filter-board"],
                    filters,
                    setFilters
                  )
              }
            ]}
            name="events-filter-menu"
          />

          {/* Game Systems */}
          {gameSystems?.length && renderGameSystemFilters ? (
            <OptionsMenu
              anchorEl={anchorEl3}
              setAnchorEl={setAnchorEl3}
              items={gameSystems.map((gameSystem) => ({
                name: `gamesystem-${gameSystem?.id}`,
                label: gameSystem.name,
                selected: filters.includes(`gamesystem-${gameSystem?.id}`),
                onClick: () =>
                  updateFilters(
                    `gamesystem-${gameSystem?.id}`,
                    false,
                    filters,
                    setFilters
                  )
              }))}
              name="gamesystem-menu"
            />
          ) : (
            <></>
          )}

          {activeEvents?.length && (
            <ActiveEventsModal
              activeEvents={activeEvents}
              setActiveEvents={setActiveEvents}
            />
          )}

          {shareModalOpen && (
            <ShareModal
              parentOpen={shareModalOpen}
              setParentOpen={setShareModalOpen}
              url={
                shareSearchPath
                  ? `${window.location.origin}${shareSearchPath}`
                  : window.location.href
              }
              linkOnly
            />
          )}
        </Grid>
      </Container>
      {(!searchReady || isSearching) && <ModalLoadingFull />}
    </Box>
  );
}
